import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Panel, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function DeletionRequests() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [notes, setNotes] = useState({});
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/admin/deletion-requests", { params: { status } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (d, approve) => {
    try { await api.post(`/admin/deletion-requests/${d.id}/decision`, { approve, note: notes[d.id] || "" }); toast.success(approve ? "Account deactivated and anonymised" : "Request rejected"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Consumers" title="Account deletion requests"
        description="Approving deactivates the login and removes the personal profile. Bills, payments and audit records are kept as required by law."
        actions={<NativeSelect value={status} onChange={setStatus} className="w-36" testId="deletion-status-filter"
          options={["pending", "approved", "rejected", "all"].map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))} />} />
      {items === null ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? <Empty>No {status === "all" ? "" : status} requests.</Empty> : (
        <div className="space-y-3">{items.map((d) => (
          <Panel key={d.id} testId={`deletion-${d.id}`}>
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <div className="min-w-0 space-y-1"><p className="font-medium break-words">{d.name} <span className="text-xs text-slate-500">{d.email}</span></p>
                <p className="text-xs text-slate-500"><span className="font-mono">{d.reference}</span> · requested {fmtDate(d.created_at)}</p>
                <p className="text-sm break-words">{d.reason || "No reason given."}</p>
                {d.review_note && <p className="text-xs text-slate-600">Review note: {d.review_note}</p>}</div>
              <div className="shrink-0 flex flex-col gap-2 md:w-72"><StatusBadge status={d.status} />
                {d.status === "pending" && <>
                  <Textarea rows={2} placeholder="Note (required to reject)" value={notes[d.id] || ""} onChange={(e) => setNotes({ ...notes, [d.id]: e.target.value })} data-testid={`deletion-note-${d.id}`} />
                  <div className="flex gap-2"><Button size="sm" variant="destructive" onClick={() => setConfirm(d)} data-testid={`deletion-approve-${d.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" disabled={(notes[d.id] || "").trim().length < 3} onClick={() => decide(d, false)} data-testid={`deletion-reject-${d.id}`}>Reject</Button></div></>}
              </div>
            </div>
          </Panel>))}</div>)}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="deletion-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this consumer's login?</AlertDialogTitle>
            <AlertDialogDescription>The account is signed out everywhere, unlinked and anonymised. This cannot be undone. Billing and payment records stay.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="deletion-confirm" onClick={() => { const d = confirm; setConfirm(null); decide(d, true); }}>Approve deletion</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
